import axios, { AxiosError, AxiosRequestConfig, isAxiosError } from 'axios';
import produce from 'immer';
import { action, makeObservable } from 'mobx';
import * as yup from 'yup';
import { oktaAuth } from '~utils/okta';
import { NotificationManager } from '../components/notification-manager';
import { AcvtStore } from './acvt';
import { AnalyticsStore } from './analytics';
import { ApplicationSelectionStore } from './application-selection';
import { AuthStore } from './auth';
import { BusinessUnitStore } from './business-unit';
import { CarWashStore } from './car-wash';
import { CareerStore } from './career';
import { CategoryStore } from './category';
import { ClientStore } from './client';
import { ConfigStore } from './config';
import { EventStore } from './events';
import { FeaturedStore } from './featured';
import { FeedbackStore } from './feedbacks';
import { GamesStore } from './games';
import { LabattGameStore } from './labatt-game';
import { NhlGameStore } from './nhl-game';
import { NPSStore } from './nps';
import { OfferStore } from './offer';
import { OnboardingStore } from './onboarding';
import { PromotionStore } from './promotion';
import { PromotionGrantStore } from './promotion-grant';
import { RechargeStore } from './recharge';
import { RppAdStore } from './rpp-ad';
import { RppPrizeCouponStore } from './rpp-prize-coupon';
import { ContestStore } from './contest';
import { StoresStore } from './stores';
import { UserStore } from './user';
import { SectionPlacementsStore } from './section-placements';
import {
    LoyaltyBannerStore,
    formatLoyaltyBannerOverlapMessage,
    isLoyaltyBannerOverlapDetails,
} from './loyalty-banner';
import { LoyaltyClubStore } from './loyalty-club';
import { Doc31Store } from './doc31';

const REACT_APP_API_URL = import.meta.env.REACT_APP_API_URL;

interface ApiErrorResponse {
    code?: string;
    message?: string;
    details?: unknown;
}

/**
 * A failed call keeps the server's `code`, `details` and HTTP `status`, so a
 * screen can react to a known refusal (a stale save, a validation result)
 * beyond the toast.
 */
export type NetworkCallResult<NetworkResponse> =
    | { data: NetworkResponse; err: null; cancelled?: false; message?: never }
    | {
          data: null;
          err: true;
          cancelled?: false;
          message?: string;
          code?: string;
          details?: unknown;
          status?: number;
      }
    | { data: null; err: true; cancelled: true; message?: never };

/** The code blocks a contest's no-purchase code does not fit, as the API names them. */
function hasBlockKeys(details: unknown): details is { blockKeys: string[] } {
    const blockKeys = (details as { blockKeys?: unknown } | null)?.blockKeys;
    return (
        Array.isArray(blockKeys) &&
        blockKeys.length > 0 &&
        blockKeys.every((key) => typeof key === 'string')
    );
}

export class RootStore {
    public userStore: UserStore;
    public configStore: ConfigStore;
    public promotionStore: PromotionStore;
    public categoryStore: CategoryStore;
    public offerStore: OfferStore;
    public featuredStore: FeaturedStore;
    public clientStore: ClientStore;
    public feedbackStore: FeedbackStore;
    public npsStore: NPSStore;
    public promotionGrantStore: PromotionGrantStore;
    public storesStore: StoresStore;
    public gamesStore: GamesStore;
    public AnalyticsStore: AnalyticsStore;
    public nhlGamesStore: NhlGameStore;
    public labattGameStore: LabattGameStore;
    public authStore = new AuthStore();
    public acvtStore: AcvtStore;
    public careerStore: CareerStore;
    public rechargeStore: RechargeStore;
    public rppAdStore: RppAdStore;
    public rppPrizeCouponStore: RppPrizeCouponStore;
    public contestStore: ContestStore;
    public doc31Store: Doc31Store;
    public carWashStore: CarWashStore;
    public onboardingStore: OnboardingStore;
    public eventStore: EventStore;
    public businessUnitStore: BusinessUnitStore;
    public applicationSelectionStore: ApplicationSelectionStore;
    public sectionPlacementsStore: SectionPlacementsStore;
    public loyaltyBannerStore: LoyaltyBannerStore;
    public loyaltyClubStore: LoyaltyClubStore;
    private cancelSource = axios.CancelToken.source();

    constructor() {
        makeObservable(this);
        this.userStore = new UserStore(this);
        this.configStore = new ConfigStore(this);
        this.promotionStore = new PromotionStore(this);
        this.categoryStore = new CategoryStore(this);
        this.offerStore = new OfferStore(this);
        this.featuredStore = new FeaturedStore(this);
        this.clientStore = new ClientStore(this);
        this.feedbackStore = new FeedbackStore(this);
        this.npsStore = new NPSStore(this);
        this.promotionGrantStore = new PromotionGrantStore(this);
        this.storesStore = new StoresStore(this);
        this.gamesStore = new GamesStore(this);
        this.AnalyticsStore = new AnalyticsStore(this);
        this.nhlGamesStore = new NhlGameStore(this);
        this.labattGameStore = new LabattGameStore(this);
        this.acvtStore = new AcvtStore(this);
        this.careerStore = new CareerStore(this);
        this.rechargeStore = new RechargeStore(this);
        this.rppAdStore = new RppAdStore(this);
        this.rppPrizeCouponStore = new RppPrizeCouponStore(this);
        this.contestStore = new ContestStore(this);
        this.doc31Store = new Doc31Store(this);
        this.carWashStore = new CarWashStore(this);
        this.onboardingStore = new OnboardingStore(this);
        this.eventStore = new EventStore(this);
        this.businessUnitStore = new BusinessUnitStore(this);
        this.applicationSelectionStore = new ApplicationSelectionStore(this);
        this.sectionPlacementsStore = new SectionPlacementsStore(this);
        this.loyaltyBannerStore = new LoyaltyBannerStore(this);
        this.loyaltyClubStore = new LoyaltyClubStore(this);
    }

    /**
     * Get standard headers for API requests including authentication and language
     * @param additionalHeaders Optional additional headers to include in the request
     * @param lang Language code (defaults to 'en')
     * @returns Headers object with JWT token and other standard headers
     */
    getHeaders(
        additionalHeaders?: Record<string, string>,
        lang = 'en'
    ): Record<string, string> {
        const token = oktaAuth.getAccessToken();
        const headers: Record<string, string> = {
            'accept-language': lang,
            ...additionalHeaders,
        };

        if (token) {
            headers.jwt = token;
        }

        return headers;
    }

    /**
     * The app's single response to a 401. Public because a blob download cannot
     * go through `makeNetworkCall`, which hands back parsed data and no headers.
     */
    @action
    handleUnauthorized(this: RootStore, message?: string): void {
        NotificationManager.showError(message || 'Authentication error');
        this.cancelSource.cancel();
        oktaAuth.signOut();
    }

    /**
     * Helper method to wrap all the chores associated with networks requests.
     *  • JWT management
     *  • Error handling
     *  • Base url
     *  • Payload validation
     *
     * @param this
     * @param opts
     * @param schema
     */
    @action
    async makeNetworkCall<NetworkResponse>(
        this: RootStore,
        opts: AxiosRequestConfig,
        schema?: yup.Schema<NetworkResponse>,
        validationOptions?: yup.ValidateOptions
    ): Promise<NetworkCallResult<NetworkResponse>> {
        try {
            const baseHeaders = this.getHeaders();

            const augmentedOpts = produce(opts, (draft) => {
                draft.headers = {
                    ...baseHeaders,
                    ...(draft.headers || {}),
                };

                draft.baseURL = `${REACT_APP_API_URL}/api-cms`;
                draft.cancelToken = this.cancelSource.token;
            });

            const response = await axios.request<NetworkResponse>(
                augmentedOpts
            );

            if (schema) {
                const validatedData = await schema.validate(
                    response.data,
                    validationOptions
                );
                return { data: validatedData, err: null };
            }
            return { data: response.data, err: null };
        } catch (e) {
            if (axios.isCancel(e)) {
                return { data: null, err: true, cancelled: true };
            }
            console.error(e);
            // Carried so a caller can show the server's own wording where the
            // toast is not enough, such as a confirmation dialog that stays on
            // screen after the toast has faded. It is the raw server message,
            // not the formatted toast text of the special-cased codes below.
            let message: string | undefined;
            let failure: {
                code?: string;
                details?: unknown;
                status?: number;
            } = {};
            if (isAxiosError(e)) {
                const status = e.response?.status;
                const errorData = e.response?.data as
                    | ApiErrorResponse
                    | undefined;
                const code = errorData?.code;
                message = errorData?.message;
                failure = { code, details: errorData?.details, status };

                if (status === 401) {
                    this.handleUnauthorized(errorData?.message);
                } else if (
                    code === 'promotion_cannot_be_deleted' ||
                    code === 'nhl_game_cannot_be_deleted'
                ) {
                    NotificationManager.showError(
                        (typeof errorData?.details === 'string'
                            ? errorData.details
                            : undefined) || 'Cannot delete this item',
                        8
                    );
                } else if (
                    code === 'loyalty_club_banner_date_overlap' &&
                    isLoyaltyBannerOverlapDetails(errorData?.details)
                ) {
                    NotificationManager.showError(
                        formatLoyaltyBannerOverlapMessage(
                            errorData.message || 'Date range overlap',
                            errorData.details
                        ),
                        10
                    );
                } else if (
                    code === 'contest_no_purchase_code_unfit' &&
                    hasBlockKeys(errorData?.details)
                ) {
                    NotificationManager.showError(
                        `${
                            errorData.message ||
                            'The no-purchase code does not fit a code block.'
                        } Blocks: ${errorData.details.blockKeys.join(', ')}.`,
                        8
                    );
                } else if (code === 'meal_deal_overlap') {
                    NotificationManager.showError(
                        errorData?.message ||
                            'This meal deal overlaps with another active one for the same app target.',
                        8
                    );
                } else {
                    NotificationManager.showError(
                        errorData?.message || 'An error occurred'
                    );
                }
            } else {
                NotificationManager.showError('Unexpected error');
            }
            return {
                data: null,
                err: true,
                cancelled: false,
                message,
                ...failure,
            };
        }
    }

    @action
    async makeMobileNetworkCall<NetworkResponse>(
        this: RootStore,
        opts: AxiosRequestConfig,
        lang: string
    ): Promise<
        { data: NetworkResponse; err: null } | { data: null; err: true }
    > {
        try {
            const baseHeaders = this.getHeaders({}, lang);

            const augmentedOpts = produce(opts, (draft) => {
                draft.headers = {
                    ...baseHeaders,
                    ...(draft.headers || {}),
                };

                draft.baseURL = `${REACT_APP_API_URL}/api-mobile`;
            });

            const response = await axios.request<NetworkResponse>(
                augmentedOpts
            );

            return { data: response.data, err: null };
        } catch (e) {
            console.error(e);
            if (isAxiosError(e)) {
                const axiosError = e as AxiosError;
                NotificationManager.showError(axiosError.message);
            } else if (!axios.isCancel(e)) {
                NotificationManager.showError('Unexpected error');
            }
            return { data: null, err: true };
        }
    }
}
